import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Grid,
  CircularProgress,
  Alert,
  Collapse,
  Slider,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SendIcon from '@mui/icons-material/Send';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import AlbumIcon from '@mui/icons-material/Album';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExplicitIcon from '@mui/icons-material/Explicit';
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import LinkIcon from '@mui/icons-material/Link';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import LabelIcon from '@mui/icons-material/Label';
import CategoryIcon from '@mui/icons-material/Category';
import CopyrightIcon from '@mui/icons-material/Copyright';
import { toastError } from '../../utils';
import { fetchMusiclink, fetchLogos } from './actions';

// ─── Animations ───

const float1 = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-18px) rotate(6deg); }
`;

const float2 = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-25px) rotate(-8deg); }
`;

const pulseGlow = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.15); opacity: 1; }
`;

const barPulse = keyframes`
  0%, 100% { height: 14px; }
  50% { height: 40px; }
`;

const spinGlow = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// ─── Styled Components ───

const PageWrapper = styled(Box)({
  position: 'relative',
  minHeight: 'calc(100vh - 64px)',
});

const PageContainer = styled(Box)({
  position: 'relative',
  zIndex: 2,
  padding: '16px 8px',
  maxWidth: 1200,
  margin: '0 auto',
});

const GlassCard = styled(Box)({
  background: 'rgba(20, 24, 40, 0.55)',
  backdropFilter: 'blur(24px) saturate(180%)',
  borderRadius: 16,
  border: '1px solid rgba(255,255,255,0.08)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.04) inset',
  padding: '16px',
});

const SearchField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(10, 14, 26, 0.6)',
    color: '#E2E8F0',
    borderRadius: 12,
    fontSize: '0.95rem',
    '& fieldset': {
      borderColor: 'rgba(255,255,255,0.1)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(156,39,176,0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#9C27B0',
      borderWidth: 2,
    },
    '&.Mui-focused': {
      boxShadow: '0 0 0 4px rgba(156,39,176,0.12)',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(226,232,240,0.6)',
    '&.Mui-focused': { color: '#9C27B0' },
  },
  '& .MuiOutlinedInput-input': {
    color: '#E2E8F0',
  },
});

const GradientButton = styled(Button)({
  background: 'linear-gradient(135deg, #9C27B0 0%, #E91E63 50%, #00BCD4 100%)',
  color: '#fff',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: 12,
  padding: '10px 28px',
  fontSize: '0.9rem',
  boxShadow: '0 4px 16px rgba(156,39,176,0.35)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #7B1FA2 0%, #C2185B 50%, #0097A7 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 20px rgba(156,39,176,0.5)',
  },
});

const ServiceCard = styled(Box)({
  background: '#FFFFFF',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 12,
  padding: '10px 14px',
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  height: 48,
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    background: '#F5F5F5',
    border: '1px solid rgba(156,39,176,0.25)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
});

const ActionButton = styled(Button)({
  textTransform: 'none',
  borderRadius: 10,
  padding: '8px 20px',
  fontWeight: 600,
  fontSize: '0.85rem',
});

const SeekSlider = styled(Slider)({
  color: '#9C27B0',
  height: 4,
  padding: '8px 0',
  '& .MuiSlider-track': {
    background: 'linear-gradient(90deg, #9C27B0, #E91E63)',
    border: 'none',
  },
  '& .MuiSlider-rail': {
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  '& .MuiSlider-thumb': {
    width: 14,
    height: 14,
    background: 'linear-gradient(135deg, #9C27B0, #E91E63)',
    boxShadow: '0 0 8px rgba(156,39,176,0.5)',
    '&:hover, &.Mui-focusVisible': {
      boxShadow: '0 0 12px rgba(156,39,176,0.7)',
    },
    '&.Mui-active': {
      boxShadow: '0 0 16px rgba(156,39,176,0.8)',
    },
  },
});

// ─── Service colors ───

const serviceColors = {
  spotify: '#1DB954',
  appleMusic: '#FC3C44',
  youtubeMusic: '#FF0000',
  youtube: '#FF0000',
  amazonMusic: '#00A8E1',
  deezer: '#A238FF',
  tidal: '#000000',
  soundcloud: '#FF5500',
  pandora: '#224099',
  iHeartRadio: '#C6002B',
  bandcamp: '#629AA9',
  napster: '#007AFF',
  jioSaavn: '#2BC5B4',
  gaana: '#E72C30',
  tiktokMusic: '#FE2C55',
  instagram: '#E1306C',
  qqMusic: '#31C27C',
  netease: '#C20C0C',
  yandex: '#FFCC00',
  audius: '#CC0FE0',
  lineMusic: '#00B900',
  awa: '#FC6631',
  kkbox: '#09CEF6',
  wynkMusic: '#1D9CEF',
  qobuz: '#296FA8',
  sevenDigital: '#E22A2A',
};

// ─── Helpers ───

function toMinutesSeconds(ms) {
  const minutes = Math.floor(ms / 60000);
  const seconds = ((ms % 60000) / 1000).toFixed(0);
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
}

function formatTime(seconds) {
  if (!seconds || !isFinite(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}

function formatServiceName(str) {
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Helpers: build share text ───

function buildLinksText(track) {
  let text = `Title: ${track.name}\n\n`;
  for (const [name, { link }] of Object.entries(track.services)) {
    text += `${formatServiceName(name)}: ${link}\n`;
  }
  text += `\nGenerated by MusiLinks - A Fronic Media Product\nhttps://musilinks.fronicmedia.com/`;
  return text;
}

function downloadAsTextFile(text, songName) {
  const sanitized = songName.replace(/[^a-zA-Z0-9]/g, '_');
  const blob = new Blob([text], { type: 'text/plain' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${sanitized}.txt`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ─── Logo helpers ───

function normalizeServiceName(str) {
  return str.toLowerCase().replace(/[^a-z0-9]/g, '');
}

const serviceKeyToLogoName = {
  amazonMusic: 'amazon',
  tiktokMusic: 'tiktok',
  instagram: 'facebook,instagram',
  netease: 'neteasecloudmusic',
  sevenDigital: '7digital',
  youtube: 'youtubemusic',
  youtubeShorts: 'youtubemusic',
};

function getLogoUrl(serviceKey, logosMap) {
  const override = serviceKeyToLogoName[serviceKey];
  if (override) return logosMap[normalizeServiceName(override)];
  return logosMap[normalizeServiceName(serviceKey)];
}

// ─── Component ───

export default function Dashboard() {
  const [isrcInput, setIsrcInput] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [copied, setCopied] = useState(false);
  const [track, setTrack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [imageBroken, setImageBroken] = useState(false);
  const [logos, setLogos] = useState({});

  // Fetch logos on mount
  useEffect(() => {
    fetchLogos()
      .then((res) => {
        const map = {};
        (res.body.logos || []).forEach((item) => {
          map[normalizeServiceName(item.name)] = item.logo;
        });
        setLogos(map);
      })
      .catch(() => {});
  }, []);

  // Audio player state
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Reset audio state when track changes
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [track]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (_, value) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = value;
    setCurrentTime(value);
  };

  const linksText = track ? buildLinksText(track) : '';

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = isrcInput.trim();
    if (!trimmed) return;
    setLoading(true);
    setSearchError('');
    setShowResults(false);
    setImageBroken(false);
    fetchMusiclink(trimmed)
      .then((res) => {
        console.log('[MusiLinks] Full API response:', res.body.result);
        setTrack(res.body.result);
        setShowResults(true);
      })
      .catch((err) => {
        const msg = err?.body?.message || err?.message || 'Invalid input. Please enter a valid Spotify link or ISRC.';
        toastError(msg);
        setSearchError(msg);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleExport = () => {
    downloadAsTextFile(linksText, track.name);
  };

  const handleCopyAll = () => {
    navigator.clipboard.writeText(linksText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleWhatsAppSend = () => {
    const phone = whatsappNumber.replace(/[^0-9+]/g, '');
    if (!phone) return;
    const encoded = encodeURIComponent(linksText);
    const url = `https://wa.me/${phone}?text=${encoded}`;
    window.open(url, '_blank');
  };

  const services = track ? Object.entries(track.services) : [];

  return (
    <>
      <Helmet>
        <title>MusiLinks</title>
      </Helmet>
      <PageWrapper>
        {/* ─── Full-screen Loading Overlay ─── */}
        {loading && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(10, 14, 26, 0.85)',
              backdropFilter: 'blur(8px)',
              gap: 2.5,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: 64,
                height: 64,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  border: '3px solid transparent',
                  borderTopColor: '#9C27B0',
                  borderRightColor: '#E91E63',
                  animation: `${spinGlow} 1s linear infinite`,
                  boxShadow: '0 0 20px rgba(156,39,176,0.3)',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: 8,
                  borderRadius: '50%',
                  border: '3px solid transparent',
                  borderBottomColor: '#00BCD4',
                  borderLeftColor: '#9C27B0',
                  animation: `${spinGlow} 1.5s linear infinite reverse`,
                  boxShadow: '0 0 15px rgba(0,188,212,0.3)',
                }}
              />
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(226,232,240,0.8)',
                fontWeight: 500,
                letterSpacing: '0.5px',
              }}
            >
              Fetching links...
            </Typography>
          </Box>
        )}

        {/* ─── Glow Orbs ─── */}
        <Box sx={{ position: 'absolute', top: '10%', left: 30, width: 250, height: 250, borderRadius: '50%', background: 'radial-gradient(circle, rgba(156,39,176,0.35) 0%, transparent 70%)', filter: 'blur(50px)', pointerEvents: 'none', animation: `${pulseGlow} 8s ease-in-out infinite` }} />
        <Box sx={{ position: 'absolute', top: '50%', left: -20, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(233,30,99,0.3) 0%, transparent 70%)', filter: 'blur(50px)', pointerEvents: 'none', animation: `${pulseGlow} 10s ease-in-out 2s infinite` }} />
        <Box sx={{ position: 'absolute', top: '20%', right: 30, width: 230, height: 230, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,188,212,0.3) 0%, transparent 70%)', filter: 'blur(50px)', pointerEvents: 'none', animation: `${pulseGlow} 9s ease-in-out 1s infinite` }} />
        <Box sx={{ position: 'absolute', top: '65%', right: -10, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(156,39,176,0.28) 0%, transparent 70%)', filter: 'blur(50px)', pointerEvents: 'none', animation: `${pulseGlow} 11s ease-in-out 3s infinite` }} />

        {/* ─── Accent Lines ─── */}
        <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: 55, width: 1, pointerEvents: 'none', background: 'linear-gradient(to bottom, transparent, rgba(156,39,176,0.2) 30%, rgba(156,39,176,0.2) 70%, transparent)' }} />
        <Box sx={{ position: 'absolute', top: 0, bottom: 0, right: 55, width: 1, pointerEvents: 'none', background: 'linear-gradient(to bottom, transparent, rgba(0,188,212,0.18) 30%, rgba(0,188,212,0.18) 70%, transparent)' }} />

        {/* ─── Floating Icons - Left ─── */}
        <Box sx={{ position: 'absolute', top: '15%', left: 25, pointerEvents: 'none', animation: `${float1} 7s ease-in-out infinite` }}>
          <MusicNoteIcon sx={{ fontSize: 34, color: 'rgba(156,39,176,0.4)' }} />
        </Box>
        <Box sx={{ position: 'absolute', top: '35%', left: 50, pointerEvents: 'none', animation: `${float2} 8s ease-in-out 1s infinite` }}>
          <LinkIcon sx={{ fontSize: 28, color: 'rgba(233,30,99,0.35)' }} />
        </Box>
        <Box sx={{ position: 'absolute', top: '55%', left: 18, pointerEvents: 'none', animation: `${float1} 6s ease-in-out 2s infinite` }}>
          <AudiotrackIcon sx={{ fontSize: 32, color: 'rgba(0,188,212,0.35)' }} />
        </Box>
        <Box sx={{ position: 'absolute', top: '75%', left: 40, pointerEvents: 'none', animation: `${float2} 9s ease-in-out 0.5s infinite` }}>
          <HeadphonesIcon sx={{ fontSize: 30, color: 'rgba(156,39,176,0.35)' }} />
        </Box>
        <Box sx={{ position: 'absolute', top: '25%', left: 12, pointerEvents: 'none', animation: `${float2} 7s ease-in-out 3s infinite` }}>
          <GraphicEqIcon sx={{ fontSize: 26, color: 'rgba(233,30,99,0.3)' }} />
        </Box>

        {/* ─── Floating Icons - Right ─── */}
        <Box sx={{ position: 'absolute', top: '18%', right: 30, pointerEvents: 'none', animation: `${float2} 8s ease-in-out 0.5s infinite` }}>
          <AlbumIcon sx={{ fontSize: 32, color: 'rgba(0,188,212,0.4)' }} />
        </Box>
        <Box sx={{ position: 'absolute', top: '40%', right: 15, pointerEvents: 'none', animation: `${float1} 7s ease-in-out 2s infinite` }}>
          <QueueMusicIcon sx={{ fontSize: 30, color: 'rgba(156,39,176,0.35)' }} />
        </Box>
        <Box sx={{ position: 'absolute', top: '60%', right: 45, pointerEvents: 'none', animation: `${float2} 9s ease-in-out infinite` }}>
          <LibraryMusicIcon sx={{ fontSize: 28, color: 'rgba(233,30,99,0.35)' }} />
        </Box>
        <Box sx={{ position: 'absolute', top: '80%', right: 20, pointerEvents: 'none', animation: `${float1} 6s ease-in-out 1.5s infinite` }}>
          <MusicNoteIcon sx={{ fontSize: 34, color: 'rgba(0,188,212,0.35)' }} />
        </Box>
        <Box sx={{ position: 'absolute', top: '48%', right: 8, pointerEvents: 'none', animation: `${float2} 8s ease-in-out 3s infinite` }}>
          <LinkIcon sx={{ fontSize: 26, color: 'rgba(156,39,176,0.3)' }} />
        </Box>

        {/* ─── Equalizer Bars - Left ─── */}
        <Box sx={{ position: 'absolute', bottom: 30, left: 65, display: 'flex', gap: '5px', alignItems: 'flex-end', pointerEvents: 'none' }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Box key={i} sx={{ width: 4, borderRadius: 2, backgroundColor: i % 2 === 0 ? 'rgba(156,39,176,0.4)' : 'rgba(233,30,99,0.35)', animation: `${barPulse} ${1.2 + i * 0.25}s ease-in-out ${i * 0.15}s infinite` }} />
          ))}
        </Box>

        {/* ─── Equalizer Bars - Right ─── */}
        <Box sx={{ position: 'absolute', bottom: 30, right: 65, display: 'flex', gap: '5px', alignItems: 'flex-end', pointerEvents: 'none' }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Box key={i} sx={{ width: 4, borderRadius: 2, backgroundColor: i % 2 === 0 ? 'rgba(0,188,212,0.4)' : 'rgba(156,39,176,0.35)', animation: `${barPulse} ${1.4 + i * 0.2}s ease-in-out ${i * 0.2 + 0.5}s infinite` }} />
          ))}
        </Box>

      <PageContainer>
        {/* ─── Search Section ─── */}
        <GlassCard sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <QueueMusicIcon sx={{ color: '#9C27B0', fontSize: 22 }} />
            <Typography
              variant="subtitle1"
              sx={{ color: '#E2E8F0', fontWeight: 700 }}
            >
              Track Lookup
            </Typography>
          </Box>
          <Box
            component="form"
            onSubmit={handleSearch}
            sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}
          >
            <SearchField
              fullWidth
              placeholder="ISRC or Spotify link like https://open.spotify.com/track/0PZyi6nAzTaTHzmgTb6Vrx"
              value={isrcInput}
              onChange={(e) => setIsrcInput(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(156,39,176,0.6)', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            <GradientButton type="submit" disabled={loading} sx={{ minWidth: 140, whiteSpace: 'nowrap' }}>
              Fetch Links
            </GradientButton>
          </Box>
        </GlassCard>

        {/* ─── Error Alert ─── */}
        <Collapse in={!!searchError}>
          <Alert severity="error" onClose={() => setSearchError('')} sx={{ mb: 2, borderRadius: 3 }}>
            {searchError}
          </Alert>
        </Collapse>

        {/* ─── Results Section ─── */}
        {showResults && track && (
          <Box sx={{ animation: 'fadeIn 0.4s ease' }}>
            {/* Track Info Card */}
            <GlassCard sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                {/* Album Art */}
                {track.image?.url && !imageBroken ? (
                  <Box
                    component="img"
                    src={track.image.url}
                    alt={track.name}
                    onError={() => setImageBroken(true)}
                    sx={{
                      width: { xs: '100%', sm: 180 },
                      height: 180,
                      minWidth: 180,
                      borderRadius: 3,
                      objectFit: 'cover',
                      border: '1px solid rgba(255,255,255,0.06)',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: { xs: '100%', sm: 180 },
                      height: 180,
                      minWidth: 180,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, rgba(156,39,176,0.3), rgba(233,30,99,0.2), rgba(0,188,212,0.2))',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      border: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <AlbumIcon sx={{ fontSize: 52, color: 'rgba(156,39,176,0.5)' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(148,163,184,0.5)', fontWeight: 500, fontSize: '0.7rem' }}>
                      No Image Available
                    </Typography>
                  </Box>
                )}

                {/* Track Details */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: '#F1F5F9',
                      mb: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {track.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {track.artists?.map((artist, idx) => (
                      <React.Fragment key={idx}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {artist.image?.url ? (
                            <Box
                              component="img"
                              src={artist.image.url}
                              alt={artist.name}
                              sx={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', border: '1px solid rgba(156,39,176,0.3)' }}
                            />
                          ) : (
                            <PersonIcon sx={{ fontSize: 28, color: 'rgba(156,39,176,0.5)' }} />
                          )}
                          <Typography variant="body1" sx={{ color: 'rgba(156,39,176,0.9)', fontWeight: 500 }}>
                            {artist.name}
                          </Typography>
                        </Box>
                        {idx < track.artists.length - 1 && (
                          <Typography variant="body1" sx={{ color: 'rgba(156,39,176,0.5)', mx: 0.3 }}>,</Typography>
                        )}
                      </React.Fragment>
                    ))}
                  </Box>

                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow sx={{ '& td': { borderColor: 'rgba(255,255,255,0.05)', py: 0.8 } }}>
                          <TableCell sx={{ color: 'rgba(148,163,184,0.7)', pl: 0, width: 130 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AlbumIcon sx={{ fontSize: 16, color: '#E91E63' }} />
                              Album
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: '#E2E8F0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {track.albums?.[0]?.image?.url && (
                                <Box
                                  component="img"
                                  src={track.albums[0].image.url}
                                  alt={track.albums[0].name}
                                  sx={{ width: 24, height: 24, borderRadius: 1, objectFit: 'cover', border: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}
                                />
                              )}
                              <Box>
                                <Typography variant="body2" sx={{ color: '#E2E8F0' }}>{track.albums?.[0]?.name}</Typography>
                                {(track.albums?.[0]?.totalTracks || track.albums?.[0]?.releaseDate) && (
                                  <Typography variant="caption" sx={{ color: 'rgba(148,163,184,0.5)', fontSize: '0.7rem' }}>
                                    {[
                                      track.albums[0].totalTracks && `${track.albums[0].totalTracks} tracks`,
                                      track.albums[0].releaseDate && String(track.albums[0].releaseDate).slice(0, 10),
                                    ].filter(Boolean).join(' · ')}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ '& td': { borderColor: 'rgba(255,255,255,0.05)', py: 0.8 } }}>
                          <TableCell sx={{ color: 'rgba(148,163,184,0.7)', pl: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AccessTimeIcon sx={{ fontSize: 16, color: '#00BCD4' }} />
                              Duration
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: '#E2E8F0' }}>{toMinutesSeconds(track.duration)}</TableCell>
                        </TableRow>
                        <TableRow sx={{ '& td': { borderColor: 'rgba(255,255,255,0.05)', py: 0.8 } }}>
                          <TableCell sx={{ color: 'rgba(148,163,184,0.7)', pl: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <MusicNoteIcon sx={{ fontSize: 16, color: '#9C27B0' }} />
                              ISRC
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: '#E2E8F0', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            {track.isrc}
                            <IconButton size="small" sx={{ ml: 1, color: 'rgba(148,163,184,0.5)' }}>
                              <ContentCopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ '& td': { borderColor: 'rgba(255,255,255,0.05)', py: 0.8 } }}>
                          <TableCell sx={{ color: 'rgba(148,163,184,0.7)', pl: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarTodayIcon sx={{ fontSize: 16, color: '#FF9800' }} />
                              Released
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: '#E2E8F0' }}>
                            {track.releaseDate ? String(track.releaseDate).slice(0, 10) : '—'}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ '& td': { borderColor: 'rgba(255,255,255,0.05)', py: 0.8 } }}>
                          <TableCell sx={{ color: 'rgba(148,163,184,0.7)', pl: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ExplicitIcon sx={{ fontSize: 16, color: 'rgba(148,163,184,0.5)' }} />
                              Explicit
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={track.isExplicit ? 'Yes' : 'No'}
                              size="small"
                              sx={{
                                bgcolor: track.isExplicit
                                  ? 'rgba(255,107,107,0.15)'
                                  : 'rgba(76,175,80,0.15)',
                                color: track.isExplicit ? '#FF6B6B' : '#4CAF50',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: 24,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                        {track.label && (
                          <TableRow sx={{ '& td': { borderColor: 'rgba(255,255,255,0.05)', py: 0.8 } }}>
                            <TableCell sx={{ color: 'rgba(148,163,184,0.7)', pl: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LabelIcon sx={{ fontSize: 16, color: '#FF9800' }} />
                                Label
                              </Box>
                            </TableCell>
                            <TableCell sx={{ color: '#E2E8F0' }}>{track.label}</TableCell>
                          </TableRow>
                        )}
                        {track.genres?.length > 0 && (
                          <TableRow sx={{ '& td': { borderColor: 'rgba(255,255,255,0.05)', py: 0.8 } }}>
                            <TableCell sx={{ color: 'rgba(148,163,184,0.7)', pl: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CategoryIcon sx={{ fontSize: 16, color: '#E91E63' }} />
                                Genre
                              </Box>
                            </TableCell>
                            <TableCell sx={{ color: '#E2E8F0' }}>{track.genres.join(', ')}</TableCell>
                          </TableRow>
                        )}
                        {track.copyright && (
                          <TableRow sx={{ '& td': { borderColor: 'transparent', py: 0.8 } }}>
                            <TableCell sx={{ color: 'rgba(148,163,184,0.7)', pl: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CopyrightIcon sx={{ fontSize: 16, color: '#94A3B8' }} />
                                Copyright
                              </Box>
                            </TableCell>
                            <TableCell sx={{ color: 'rgba(148,163,184,0.6)', fontSize: '0.8rem' }}>{track.copyright}</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {track.previewUrl?.length > 0 && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <HeadphonesIcon sx={{ fontSize: 18, color: '#9C27B0' }} />
                        <Typography variant="caption" sx={{ color: 'rgba(148,163,184,0.7)', fontWeight: 600 }}>Preview</Typography>
                      </Box>
                      <audio
                        ref={audioRef}
                        src={Array.isArray(track.previewUrl) ? track.previewUrl[0] : track.previewUrl}
                        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
                        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
                        onEnded={() => { setIsPlaying(false); setCurrentTime(0); }}
                        style={{ display: 'none' }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          background: 'rgba(10, 14, 26, 0.6)',
                          borderRadius: 3,
                          padding: '8px 14px',
                          border: '1px solid rgba(255,255,255,0.06)',
                        }}
                      >
                        <IconButton
                          onClick={togglePlay}
                          sx={{
                            width: 36,
                            height: 36,
                            background: 'linear-gradient(135deg, #9C27B0, #E91E63)',
                            color: '#fff',
                            boxShadow: '0 0 12px rgba(156,39,176,0.4)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #7B1FA2, #C2185B)',
                              boxShadow: '0 0 18px rgba(156,39,176,0.6)',
                            },
                          }}
                        >
                          {isPlaying ? <PauseIcon sx={{ fontSize: 20 }} /> : <PlayArrowIcon sx={{ fontSize: 20 }} />}
                        </IconButton>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(226,232,240,0.7)',
                            fontFamily: 'monospace',
                            fontSize: '0.78rem',
                            minWidth: 70,
                            textAlign: 'center',
                            userSelect: 'none',
                          }}
                        >
                          {formatTime(currentTime)} / {formatTime(duration)}
                        </Typography>
                        <SeekSlider
                          value={currentTime}
                          max={duration || 1}
                          onChange={handleSeek}
                          sx={{ flex: 1 }}
                        />
                        <VolumeUpIcon sx={{ fontSize: 18, color: 'rgba(0,188,212,0.5)' }} />
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </GlassCard>

            {/* ─── Streaming Services ─── */}
            <GlassCard sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ color: '#E2E8F0', fontWeight: 700 }}>
                  Streaming Links
                </Typography>
                <Chip
                  label={`${services.length} platforms`}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(156,39,176,0.15)',
                    color: '#9C27B0',
                    fontWeight: 600,
                    fontSize: '0.72rem',
                  }}
                />
              </Box>

              <Grid container spacing={2}>
                {services.map(([name, service]) => {
                  const color = serviceColors[name] || '#9C27B0';
                  const logoUrl = getLogoUrl(name, logos);
                  return (
                    <Grid item xs={6} sm={4} md={3} key={name}>
                      <ServiceCard
                        component="a"
                        href={service.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textDecoration: 'none' }}
                      >
                        {logoUrl ? (
                          <Box
                            component="img"
                            src={logoUrl}
                            alt={formatServiceName(name)}
                            sx={{ width: 52, height: 52, borderRadius: 1.5, objectFit: 'contain', flexShrink: 0 }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 52,
                              height: 52,
                              borderRadius: 1.5,
                              backgroundColor: `${color}22`,
                              border: `1px solid ${color}44`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1.1rem',
                              color: color,
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          >
                            {formatServiceName(name).charAt(0)}
                          </Box>
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#1A1A1A',
                            fontWeight: 600,
                            fontSize: '0.88rem',
                            lineHeight: 1.3,
                          }}
                        >
                          {formatServiceName(name)}
                        </Typography>
                      </ServiceCard>
                    </Grid>
                  );
                })}
              </Grid>
            </GlassCard>

            {/* ─── Actions: Export & WhatsApp ─── */}
            <GlassCard>
              <Typography variant="subtitle1" sx={{ color: '#E2E8F0', fontWeight: 700, mb: 2 }}>
                Share & Export
              </Typography>

              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                <ActionButton
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExport}
                  sx={{
                    flex: 1,
                    minWidth: { xs: 'calc(50% - 6px)', sm: 0 },
                    borderColor: 'rgba(0,188,212,0.4)',
                    color: '#00BCD4',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      borderColor: '#00BCD4',
                      background: 'rgba(0,188,212,0.08)',
                    },
                  }}
                >
                  Export .txt
                </ActionButton>
                <ActionButton
                  variant="outlined"
                  startIcon={copied ? null : <ContentCopyIcon />}
                  onClick={handleCopyAll}
                  sx={{
                    flex: 1,
                    minWidth: { xs: 'calc(50% - 6px)', sm: 0 },
                    borderColor: copied ? 'rgba(76,175,80,0.5)' : 'rgba(156,39,176,0.4)',
                    color: copied ? '#4CAF50' : '#9C27B0',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      borderColor: copied ? '#4CAF50' : '#9C27B0',
                      background: copied ? 'rgba(76,175,80,0.08)' : 'rgba(156,39,176,0.08)',
                    },
                  }}
                >
                  {copied ? 'Copied!' : 'Copy All'}
                </ActionButton>
                <SearchField
                  placeholder="+91 9876543210"
                  value={whatsappNumber}
                  onChange={(e) => setWhatsappNumber(e.target.value)}
                  size="small"
                  sx={{ flex: 1, minWidth: { xs: 'calc(50% - 6px)', sm: 0 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <WhatsAppIcon sx={{ color: '#25D366', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <ActionButton
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handleWhatsAppSend}
                  disabled={!whatsappNumber.trim()}
                  sx={{
                    flex: 1,
                    minWidth: { xs: 'calc(50% - 6px)', sm: 0 },
                    bgcolor: '#25D366',
                    color: '#fff',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      bgcolor: '#1DA851',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'rgba(37,211,102,0.3)',
                      color: 'rgba(255,255,255,0.5)',
                      pointerEvents: 'auto',
                      cursor: 'not-allowed',
                    },
                  }}
                >
                  Send
                </ActionButton>
              </Box>

            </GlassCard>
          </Box>
        )}

        {/* ─── Empty State ─── */}
        {!showResults && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 'calc(100vh - 250px)',
              opacity: 0.5,
            }}
          >
            <MusicNoteIcon sx={{ fontSize: 56, color: '#9C27B0', mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'rgba(148,163,184,0.7)', fontWeight: 500 }}>
              Paste a Spotify Track Link or ISRC to get started
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(148,163,184,0.4)', mt: 0.5 }}>
              Fetch streaming links across 24+ platforms instantly
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(148,163,184,0.3)', mt: 0.5 }}>
              Example - https://open.spotify.com/track/0PZyi6nAzTaTHzmgTb6Vrx
            </Typography>
          </Box>
        )}
      </PageContainer>
      </PageWrapper>
    </>
  );
}
